import { RichTextEditor } from '@levitate/rich-text';
import type { IRichContentEditorState } from '@ViewModels';
import { useState } from 'react';
import { THEME } from '@Theme';
import { MEETING_MERGE_FIELD_TOKENS } from './meetingMergeFields';
import { MeetingMergeFieldMenu } from './MeetingMergeFieldMenu';
import { editorHtmlToMeetingState, meetingStateToEditorHtml, MEETING_NOTE_BLOCK_STYLE } from './meetingNoteContent';

// The shared editor ships an embed-tuned borderless surface; give it the app's standard field
// border so it reads like the other form controls in the meeting config.
const SURFACE_STYLE = { border: THEME.common.border };

interface IProps {
	contentState: IRichContentEditorState;
	onContentStateChanged: (state: IRichContentEditorState) => void;
	onBlur?: () => void;
	placeholder?: string;
}

/**
 * tiptap-based meeting-note editor (replaces the legacy TinyMCE RichContentDocumentEditor for
 * https://github.com/Real-Magic/Levitate-FrontEnd/issues/9502). Renders merge fields as stylized
 * pills while persisting the exact stored contract: meeting tokens as raw `{{...}}`, contact fields
 * as `data-placeholder` spans. Matches the legacy editor's behavior — merge-field menu only, no
 * formatting toolbar.
 */
export const MeetingNoteEditor = ({ contentState, onContentStateChanged, onBlur, placeholder }: IProps) => {
	// Convert the stored note once; later edits flow out through onChange, so the editor owns the
	// content from here and must not be reset by parent re-renders.
	const [initialContent] = useState(() => meetingStateToEditorHtml(contentState));

	return (
		<RichTextEditor
			initialContent={initialContent}
			mergeFieldTokens={MEETING_MERGE_FIELD_TOKENS}
			paragraphStyle={MEETING_NOTE_BLOCK_STYLE}
			placeholder={placeholder}
			showFormatting={false}
			surfaceStyle={SURFACE_STYLE}
			renderMergeField={insert => <MeetingMergeFieldMenu onInsert={insert} />}
			onBlur={onBlur}
			onChange={html => onContentStateChanged(editorHtmlToMeetingState(html))}
		/>
	);
};
